import { isNonNullable } from '@nucleus/lib-shape';
import { DynamicPostListDataSource, PostBlockWeb, StaticPostListDataSource } from '@nucleus/types/web';
import { PostIndexDocumentWeb, WebHostingRouterInput } from '@nucleus/web-hosting';
import invariant from 'invariant';
import { flatMap as _flatMap, pick as _pick } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { usePostPath } from '../hooks/usePostPath';
import { usePosts } from '../hooks/usePosts';
import { getCollectionTypeFromId } from '../lib/collection';

type PostListInput = WebHostingRouterInput['postHub']['post']['list'];
type UsePosts = ReturnType<typeof usePosts>;

type Context = Pick<UsePosts, 'status' | 'hasNextPage' | 'fetchNextPage' | 'isFetchingNextPage'> & {
  posts: PostIndexDocumentWeb[];
  currentlyPlayingPost?: PostIndexDocumentWeb;
  order: NonNullable<PostListInput['order']>;
  setOrder: React.Dispatch<React.SetStateAction<NonNullable<PostListInput['order']>>>;
  orderBy: NonNullable<PostListInput['orderBy']>;
  setOrderBy: React.Dispatch<React.SetStateAction<NonNullable<PostListInput['orderBy']>>>;
  availableToOrderBy: NonNullable<PostListInput['orderBy']>[];
  getPostPath: (post: PostIndexDocumentWeb) => string;
};

const Context = React.createContext<Context>(null!);
Context.displayName = 'PostListController';

type Props = { children?: React.ReactNode } & Options;

export const PostListController = ({ children, ...props }: Props): JSX.Element => {
  return <Context.Provider value={useController(props)}>{children}</Context.Provider>;
};

export const usePostListController = (): Context => {
  invariant(
    useContext(Context) !== null,
    'usePostListController() may be used only in the context of a <PostListController> object'
  );
  return useContext(Context);
};

type Options = {
  engineId: string;
  basePath: string;
  currentlyPlayingPostId?: string;
  params?: Omit<PostListInput, 'hubId'>;
  isLoadMoreEnabled?: boolean;
};

const useController = ({ basePath, ...options }: Options): Context => {
  const initialOrderBy = options.params?.orderBy ?? 'date';
  const [orderBy, setOrderBy] = useState<Context['orderBy']>(initialOrderBy);

  const initialOrder = options.params?.order ?? 'desc';
  const [order, setOrder] = useState<Context['order']>(initialOrder);

  useEffect(() => {
    setOrder((current) => options.params?.order ?? current);
  }, [options.params?.order]);

  const { data, status, hasNextPage, fetchNextPage, isFetchingNextPage } = usePosts(options.engineId, {
    ...options.params,
    order: order,
    orderBy: orderBy,
  });

  const getPostPath = usePostPath(basePath);

  let availableToOrderBy: NonNullable<PostListInput['orderBy']>[] = ['date'];

  if (options.params?.postCollection !== undefined) {
    availableToOrderBy.push('postCollection');
  }

  if (options.params?.ids !== undefined) {
    availableToOrderBy = [];
  }

  const posts = _flatMap(data?.pages, 'posts');
  const currentlyPlaying = posts.find((post) => post.id === options.currentlyPlayingPostId);

  return {
    currentlyPlayingPost: currentlyPlaying,
    posts: posts,
    status: status,
    hasNextPage: hasNextPage,
    fetchNextPage: fetchNextPage,
    isFetchingNextPage: isFetchingNextPage,
    order: order,
    setOrder: setOrder,
    orderBy: orderBy,
    setOrderBy: setOrderBy,
    availableToOrderBy: availableToOrderBy,
    getPostPath: getPostPath,
  };
};

const getQueryParams = (dataSource: PostBlockWeb['dataSource']) => {
  const safeProps = _pick(dataSource, 'limit', 'order', 'orderBy');

  const paramEntries = Object.entries(safeProps).map(([key, value]) => [key, value]);

  return Object.fromEntries(paramEntries);
};

export const usePostBlockDataSourcePostListParams = ({
  dataSource,
  items,
}: PostBlockWeb): Omit<PostListInput, 'hubId'> | undefined => {
  const baseParams = getQueryParams(dataSource);

  if (isStaticPostDataSource(dataSource)) {
    return {
      ...baseParams,
      ids: items?.map((item) => item.sourceId).join(','),
      order: 'asc',
    };
  }

  if (isDynamicPostListDataSource(dataSource, items) && dataSource.sourceId !== undefined) {
    const key = getCollectionTypeFromId(dataSource.sourceId);

    if (key === undefined) {
      return baseParams;
    }

    const collectionKeyMap: Record<string, string> = {
      postCollection: 'postCollection',
      tag: 'tag',
      author: 'author',
    };

    const apiKey = collectionKeyMap[key];

    if (apiKey === undefined) {
      return baseParams;
    }

    return {
      ...baseParams,
      [apiKey]: dataSource.sourceId,
      order: dataSource.order,
      orderBy: (dataSource.orderBy ?? key === 'postCollection') ? 'postCollection' : undefined,
    };
  }

  if (isDynamicPostListDataSource(dataSource, items) && items !== undefined && items.length > 0) {
    const key = getCollectionTypeFromId(items[0].sourceId);

    if (key === undefined) {
      return baseParams;
    }

    const sourceIds = items
      .map((item) => item.sourceId)
      .filter(isNonNullable)
      .join(',');

    const collectionKeyMap: Record<string, string> = {
      postCollection: 'postCollection',
      tag: 'tag',
      author: 'author',
    };

    const apiKey = collectionKeyMap[key];

    if (apiKey === undefined) {
      return baseParams;
    }

    return {
      ...baseParams,
      [apiKey]: sourceIds,
      order: dataSource.order,
      orderBy: 'date',
    };
  }

  return baseParams;
};

export const useCollectionDataSourceParams = (collectionId: string): Omit<PostListInput, 'hubId'> | undefined => {
  const key = getCollectionTypeFromId(collectionId);

  if (key === undefined) {
    return;
  }

  const collectionKeyMap: Record<string, string> = {
    postCollection: 'postCollection',
    tag: 'tag',
    author: 'author',
  };

  const apiKey = collectionKeyMap[key];

  if (apiKey === undefined) {
    return;
  }

  return {
    [apiKey]: collectionId,
    orderBy: key === 'postCollection' ? 'postCollection' : undefined,
  };
};

export const isStaticPostDataSource = (dataSource: any): dataSource is StaticPostListDataSource => {
  return dataSource?.type === 'static' && dataSource?.itemType === 'post';
};

export const isDynamicPostListDataSource = (
  dataSource: any,
  items?: any[]
): dataSource is DynamicPostListDataSource => {
  const hasItems = items !== undefined && items.length > 0;
  const isCollectionList = ['postCollection', 'author', 'topic'].includes(dataSource?.itemType);
  return dataSource?.type === 'dynamic' && (dataSource?.itemType === 'post' || (isCollectionList && hasItems));
};
